import { Box, Container, Typography, Divider, Paper } from '@mui/material';

function Terms() {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h2" gutterBottom textAlign="center" sx={{ mb: 6, fontWeight: 'bold' }}>
          IX-Study – Terms of Service
        </Typography>

        <Typography paragraph sx={{ mb: 4, fontStyle: 'italic', color: 'text.secondary', textAlign: 'center' }}>
          Last Updated: April 18, 2025
        </Typography>

        <Typography paragraph sx={{ mb: 4 }}>
          Welcome to <strong>IX-Study</strong>! By using this app, you agree to follow these Terms of Service. If you're under 18, get permission from a parent or guardian before using the platform.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          1. What IX-Study Does
        </Typography>
        <Typography paragraph>
          IX-Study is your AI-powered study buddy. We help you organize your study life with schedules, notes, flashcards, quizzes, and more—all using smart tech to level up your learning. It's here to support, not replace, your education.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          2. Third-Party Services & Open Source Licenses
        </Typography>
        <Typography paragraph>
          IX-Study uses external AI, infrastructure, and open-source libraries to power its features. By using this app, you also agree to the terms and privacy policies of the following providers and licenses:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <Typography component="li" paragraph>
            <strong>Together AI</strong>: 
            <a href="https://www.together.ai/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Use</a> | 
            <a href="https://www.together.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a><br/>
            (AI model inference and infrastructure)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Meta (LLaMA Models)</strong>: 
            <a href="https://ai.meta.com/llama/license/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Meta AI Terms</a> | 
            <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Meta Privacy Policy</a><br/>
            (Large language models for AI features)
          </Typography>
          <Typography component="li" paragraph>
            <strong>OpenRouter</strong>: 
            <a href="https://openrouter.ai/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms</a> | 
            <a href="https://openrouter.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a><br/>
            (AI API routing and access to multiple models)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Qroq</strong>: 
            <a href="https://qroq.ai/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms</a> | 
            <a href="https://qroq.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a><br/>
            (AI-powered features and infrastructure)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Apache License 2.0</strong>: 
            <a href="https://www.apache.org/licenses/LICENSE-2.0" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Full License Text</a><br/>
            (Some libraries and dependencies are licensed under Apache 2.0)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Mistral AI</strong>: 
            <a href="https://mistral.ai/terms-of-use/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Use</a> | 
            <a href="https://mistral.ai/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a><br/>
            (Large language models for AI features)
          </Typography>
          <Typography component="li" paragraph>
            <strong>MIT License</strong>: 
            <a href="https://opensource.org/licenses/MIT" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Full License Text</a><br/>
            (Some libraries and dependencies are licensed under MIT)
          </Typography>
        </Typography>
        <Typography paragraph>
          These services and licenses help power our backend and frontend. When using IX-Study, you're also agreeing to their terms and policies.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          3. Your Responsibilities
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <Typography component="li" paragraph>Use the platform legally and responsibly.</Typography>
          <Typography component="li" paragraph>Do not abuse, spam, or reverse-engineer the system.</Typography>
          <Typography component="li" paragraph>Keep your inputs clean and do not feed harmful, illegal, or unsafe content into the AI.</Typography>
          <Typography component="li" paragraph>Respect other users and do not disrupt the service for others.</Typography>
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          4. Account Security
        </Typography>
        <Typography paragraph>
          You are responsible for maintaining the security of your account. Do not share your login credentials with others. If you suspect unauthorized activity, contact us immediately.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          5. Intellectual Property
        </Typography>
        <Typography paragraph>
          All content, code, and branding on IX-Study are the intellectual property of Nikhilesh Rao unless otherwise stated. You may not copy, modify, or distribute any part of the app without permission.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          6. Limitation of Liability
        </Typography>
        <Typography paragraph>
          IX-Study is provided "as is" without warranties of any kind. We are not liable for any damages or losses resulting from your use of the app. Use the app at your own risk.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          7. Changes to These Terms
        </Typography>
        <Typography paragraph>
          We may update these Terms of Service from time to time. Changes will be posted on this page, and your continued use of IX-Study means you accept the updated terms.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          8. AI Isn't Perfect
        </Typography>
        <Typography paragraph>
          AI gives solid help—but it's not flawless. Always double-check critical facts, especially for schoolwork. We're not responsible for any errors or missed answers.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          9. Privacy & Data
        </Typography>
        <Typography paragraph>
          We care about your privacy. We don't sell or share personal data. Some usage data (like how often you use a feature) may be collected to improve your experience.
        </Typography>
        <Typography paragraph>
          More info in our <a href="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a>.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          10. Changes & Updates
        </Typography>
        <Typography paragraph>
          We might change these Terms or parts of the app as we grow. If anything major changes, we'll let you know. By continuing to use IX-Study, you accept the latest version of the terms.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          11. Account Suspension
        </Typography>
        <Typography paragraph>
          If you break the rules, we can suspend or ban your access. We're chill, but we keep it safe and respectful for all users.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          12. Disclaimer & Limitations
        </Typography>
        <Typography paragraph>
          IX-Study is offered "as is." We try our best, but we can't promise no bugs, no downtime, or perfect AI answers. Use it at your own risk.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          13. Contact Us
        </Typography>
        <Typography paragraph>
          Got questions, suggestions, or issues?<br />
          📧 <a href="mailto:TIXOhelp@protonmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>TIXOhelp@protonmail.com</a>
        </Typography>
      </Paper>
    </Container>
  );
}

export default Terms;